/**
 * Sidebar — unified rail that replaces GlobalRail + CockpitSidebar +
 * GlobalHeader's WorkspaceSelectorDropdown.
 *
 * Mode (portfolio | scoped) is derived from the URL path (per I-2).
 * Same shell composition in both modes; only nav content swaps (per I-7).
 *
 * Spec §5.
 */

import clsx from "clsx";
import {
  ChevronsUpDown,
  HelpCircle,
  LogOut,
  Search,
  Settings,
  Shield,
} from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { useCommandPalette } from "@/context/CommandPaletteContext";
import {
  cockpitNavConfig,
  portfolioNavItems,
  type NavItem,
  type NavSection,
} from "@/nav/navConfig";
import {
  canAccessDonorLab,
  getDonorLabAccessReason,
  useIsSuperAdmin,
} from "@/permissions";
import { buildWorkspacePath } from "@/utils/routes";

import { HelpDrawer } from "./HelpDrawer";
import { SwitcherPopover } from "./SwitcherPopover";

// =============================================================================
// TYPES
// =============================================================================

interface ItemState {
  enabled: boolean;
  reason?: string;
}

// =============================================================================
// MODE DERIVATION
// =============================================================================

function useDerivedMode(): {
  mode: "portfolio" | "scoped";
  slug: string | null;
} {
  const location = useLocation();
  if (location.pathname.startsWith("/w/")) {
    const slug = location.pathname.split("/")[2] || null;
    return { mode: "scoped", slug };
  }
  return { mode: "portfolio", slug: null };
}

// =============================================================================
// WORKSPACE NAV — NAV ITEM ROW
// =============================================================================

interface WorkspaceNavItemRowProps {
  item: NavItem;
  clientSlug: string;
  state: ItemState;
}

function WorkspaceNavItemRow({
  item,
  clientSlug,
  state,
}: WorkspaceNavItemRowProps) {
  const href = buildWorkspacePath(clientSlug, item.path);

  if (!state.enabled) {
    return (
      <div
        className={clsx(
          "flex items-center gap-3 mx-1 px-3 py-2 rounded-lg",
          "text-[var(--ws-text-secondary)]/50 cursor-not-allowed",
        )}
        title={state.reason}
      >
        {item.icon}
        <span className="text-sm">{item.label}</span>
      </div>
    );
  }

  return (
    <NavLink
      to={href}
      end={item.path === ""}
      className={({ isActive }) =>
        clsx(
          "flex items-center gap-3 mx-1 px-3 py-2 rounded-lg",
          "transition-colors duration-150",
          isActive
            ? "text-[var(--ws-accent)] bg-[var(--ws-accent-soft)]"
            : "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)] hover:bg-[var(--ws-surface-subtle)]",
        )
      }
    >
      {item.icon}
      <span className="text-sm">{item.label}</span>
      {item.badge && (
        <span
          className={clsx(
            "ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded",
            "bg-[var(--ws-accent)]/10 text-[var(--ws-accent)]",
          )}
        >
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

// =============================================================================
// WORKSPACE NAV — SECTIONS (ported from CockpitSidebar, collapse stripped)
// =============================================================================

interface WorkspaceNavSectionsProps {
  slug: string;
  user: ReturnType<typeof useAuth>["user"];
  currentClient: ReturnType<typeof useClient>["currentClient"];
}

function WorkspaceNavSections({
  slug,
  user,
  currentClient,
}: WorkspaceNavSectionsProps) {
  // Capability gate logic ported from CockpitSidebar.getItemState
  const getItemState = (item: NavItem): ItemState => {
    if (!item.requiresCapabilityCheck) {
      return { enabled: true };
    }
    if (item.id === "donor-lab") {
      const canAccess = canAccessDonorLab(user, currentClient);
      const reason = getDonorLabAccessReason(user, currentClient);
      return { enabled: canAccess, reason: reason || undefined };
    }
    if (item.id === "blueprints") {
      return { enabled: true };
    }
    return { enabled: true };
  };

  return (
    <>
      {/* Header items (Command Center, Agent Inbox) */}
      <div className="py-1">
        {cockpitNavConfig.header.map((item) => (
          <WorkspaceNavItemRow
            key={item.id}
            item={item}
            clientSlug={slug}
            state={getItemState(item)}
          />
        ))}
      </div>

      {/* Main sections — collapse stripped, all visible */}
      {cockpitNavConfig.sections.map((section: NavSection, index: number) => {
        const prevSection =
          index > 0 ? cockpitNavConfig.sections[index - 1] : null;
        const isGroupBoundary =
          prevSection && prevSection.group !== section.group;

        return (
          <div
            key={section.id}
            className={clsx(
              "border-t border-[var(--ws-border-soft)]",
              isGroupBoundary ? "pt-3 mt-4" : "pt-2 mt-2",
              index === 0 && "border-t-0 pt-0 mt-0",
            )}
          >
            {section.label && (
              <div
                className={clsx(
                  "w-full flex items-center px-4 py-2",
                  "text-[10px] font-semibold uppercase tracking-wider",
                  "text-[var(--ws-text-secondary)]",
                )}
              >
                {section.label}
              </div>
            )}
            {section.items.map((item) => (
              <WorkspaceNavItemRow
                key={item.id}
                item={item}
                clientSlug={slug}
                state={getItemState(item)}
              />
            ))}
          </div>
        );
      })}

      {/* Configure section */}
      <div className="border-t border-[var(--ws-border-soft)] pt-2 mt-3">
        {cockpitNavConfig.configure.label && (
          <div
            className={clsx(
              "w-full flex items-center px-4 py-2",
              "text-[10px] font-semibold uppercase tracking-wider",
              "text-[var(--ws-text-secondary)]",
            )}
          >
            {cockpitNavConfig.configure.label}
          </div>
        )}
        {cockpitNavConfig.configure.items.map((item) => (
          <WorkspaceNavItemRow
            key={item.id}
            item={item}
            clientSlug={slug}
            state={getItemState(item)}
          />
        ))}
      </div>

      {/* Track15 cross-client section — always visible at bottom in scoped mode */}
      <div
        data-testid="rail-track15-section"
        className="border-t border-[var(--ws-border-soft)] pt-2 mt-3"
      >
        <div
          className={clsx(
            "w-full flex items-center px-4 py-2",
            "text-[10px] font-semibold uppercase tracking-wider",
            "text-[var(--ws-text-secondary)]",
          )}
        >
          Track15
        </div>
        {cockpitNavConfig.pinned.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) =>
              clsx(
                "flex items-center gap-3 mx-1 px-3 py-2 rounded-lg",
                "transition-colors duration-150",
                isActive
                  ? "text-[var(--ws-accent)] bg-[var(--ws-accent-soft)]"
                  : "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)] hover:bg-[var(--ws-surface-subtle)]",
              )
            }
          >
            {item.icon}
            <span className="text-sm">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </>
  );
}

// =============================================================================
// MAIN SIDEBAR COMPONENT
// =============================================================================

export function Sidebar() {
  const { mode, slug } = useDerivedMode();
  const { currentClient, recentClients } = useClient();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { open: openPalette } = useCommandPalette();
  const isSuperAdmin = useIsSuperAdmin();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // Label for the section header and switcher pill
  const contextLabel =
    mode === "portfolio" ? "Portfolio" : (currentClient?.name ?? slug ?? "");

  // Switcher handlers (per spec §5.3)
  const handleSelectAllClients = () => {
    setSwitcherOpen(false);
    const path = location.pathname;
    if (path.startsWith("/w/")) {
      const tail = path.split("/").slice(3).join("/");
      // mode-dual surface (actions) → drop scope prefix
      if (tail === "actions") {
        void navigate(`/${tail}`);
      } else {
        void navigate("/home");
      }
    }
    // /cockpit filters in place (?focus=) — "All Clients" clears the filter.
    else if (path === "/cockpit" && location.search) {
      void navigate("/cockpit");
    }
    // Otherwise already portfolio: no-op (already there)
  };

  const handleSelectClient = (newSlug: string) => {
    setSwitcherOpen(false);
    const path = location.pathname;
    if (path === "/cockpit") {
      // /cockpit is the cross-client portfolio view: narrow it in place rather
      // than leaving for the workspace (Dave, 2026-09-21).
      void navigate(`/cockpit?focus=${encodeURIComponent(newSlug)}`);
    } else if (path === "/actions") {
      // mode-dual: take the portfolio path into workspace scope
      void navigate(`/w/${newSlug}${path}`);
    } else if (path.startsWith("/w/")) {
      // Preserve current route section under new slug
      const sections = path.split("/").slice(3);
      void navigate(`/w/${newSlug}/${sections.join("/")}`);
    } else {
      // Portfolio-only or no equivalent → go to workspace root
      void navigate(`/w/${newSlug}/`);
    }
  };

  return (
    <aside
      className={clsx(
        "flex flex-col flex-shrink-0 h-screen w-52",
        "bg-[var(--ws-surface)] border-r border-[var(--ws-border-soft)]",
      )}
    >
      {/* ── Brand ──────────────────────────────────────────── */}
      <div className="px-4 py-3 border-b border-[var(--ws-border-soft)]">
        <span className="text-xs uppercase tracking-wide opacity-60 font-medium">
          Track15 Nexus
        </span>
      </div>

      {/* ── Switcher pill ───────────────────────────────────── */}
      <div className="px-2 py-2 relative">
        <button
          type="button"
          onClick={() => setSwitcherOpen((v) => !v)}
          className={clsx(
            "w-full flex items-center justify-between gap-2 px-3 py-2 rounded",
            "bg-[var(--ws-accent-soft)] hover:bg-[var(--ws-accent)]/10",
            "text-sm font-medium transition-colors",
          )}
        >
          <span className="text-sm font-medium truncate flex-1 text-left">
            {contextLabel}
          </span>
          <ChevronsUpDown
            className="h-4 w-4 opacity-60 flex-shrink-0"
            aria-hidden="true"
          />
        </button>

        {switcherOpen && (
          <>
            {/* Dismiss backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setSwitcherOpen(false)}
              aria-hidden="true"
            />
            <div
              className={clsx(
                "absolute left-2 right-2 top-full mt-1 z-50",
                "bg-[var(--ws-surface)] border border-[var(--ws-border)]",
                "rounded shadow-lg",
              )}
            >
              <SwitcherPopover
                recents={recentClients}
                currentMode={mode}
                currentClientId={currentClient?.id ?? null}
                onSelectAllClients={handleSelectAllClients}
                onSelectClient={handleSelectClient}
              />
            </div>
          </>
        )}
      </div>

      {/* ── Visual separator (context → capability boundary) ─ */}
      <div className="border-t border-[var(--ws-border-soft)] mx-2 mb-1" />

      {/* ── Section header ──────────────────────────────────── */}
      <div
        data-testid="sidebar-section-header"
        className="px-4 py-1.5 text-sm font-semibold text-[var(--ws-text)]"
      >
        {contextLabel}
      </div>

      {/* ── Mode-dependent nav ──────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto px-1 py-1">
        {mode === "portfolio" ? (
          portfolioNavItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                clsx(
                  "flex items-center gap-3 mx-1 px-3 py-2 rounded-lg text-sm",
                  "transition-colors duration-150",
                  isActive
                    ? "text-[var(--ws-accent)] bg-[var(--ws-accent-soft)] font-medium"
                    : "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)] hover:bg-[var(--ws-surface-subtle)]",
                )
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))
        ) : (
          <>
            {/* ‹ Clients back link — top of scoped rail, always visible */}
            <Link
              to="/"
              aria-label="Back to clients"
              data-testid="rail-back-to-clients"
              className={clsx(
                "flex items-center gap-1 mx-1 px-3 py-2 rounded-lg text-sm",
                "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)]",
                "hover:bg-[var(--ws-surface-subtle)] transition-colors duration-150",
              )}
            >
              <span aria-hidden="true">‹</span> Clients
            </Link>
            <WorkspaceNavSections
              slug={slug ?? ""}
              user={user}
              currentClient={currentClient}
            />
          </>
        )}
      </nav>

      {/* ── Cmd+K quiet input affordance ────────────────────── */}
      <div className="px-2 py-2">
        <button
          type="button"
          onClick={openPalette}
          aria-label="Search"
          className={clsx(
            "w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded",
            "bg-transparent border border-[var(--ws-border-soft)]",
            "text-xs opacity-70 hover:opacity-100 transition-opacity",
            "text-[var(--ws-text-secondary)]",
          )}
        >
          <span className="flex items-center gap-2">
            <Search className="h-3 w-3" />
            <span>Search…</span>
          </span>
          <kbd className="text-[10px] font-mono">⌘K</kbd>
        </button>
      </div>

      {/* ── Footer ──────────────────────────────────────────── */}
      <div
        className={clsx(
          "border-t border-[var(--ws-border-soft)]",
          "px-2 py-2 flex flex-col gap-0.5",
        )}
      >
        {/* Help */}
        <button
          type="button"
          aria-label="Help"
          onClick={() => setHelpOpen(true)}
          className={clsx(
            "flex items-center gap-2 px-3 py-2 rounded text-sm",
            "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)]",
            "hover:bg-[var(--ws-surface-subtle)] transition-colors",
          )}
        >
          <HelpCircle className="h-4 w-4 flex-shrink-0" />
          <span>Help</span>
        </button>

        {/* Admin — super-admin gated */}
        {isSuperAdmin && (
          <Link
            to="/admin/clients"
            className={clsx(
              "flex items-center gap-2 px-3 py-2 rounded text-sm",
              "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)]",
              "hover:bg-[var(--ws-surface-subtle)] transition-colors",
            )}
          >
            <Shield className="h-4 w-4 flex-shrink-0" />
            <span>Admin</span>
          </Link>
        )}

        {/* Profile with identity + popover for Settings / Sign out */}
        <div className="relative">
          <button
            type="button"
            aria-label={user?.email ?? "Profile"}
            onClick={() => setProfileOpen((v) => !v)}
            className={clsx(
              "w-full flex items-center gap-2 px-3 py-2 rounded text-sm",
              "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)]",
              "hover:bg-[var(--ws-surface-subtle)] transition-colors",
            )}
          >
            {/* Avatar / initials */}
            <span
              className={clsx(
                "flex-shrink-0 w-6 h-6 rounded-full",
                "bg-[var(--ws-accent)] text-white",
                "flex items-center justify-center",
                "text-[10px] font-semibold",
              )}
            >
              {(user?.email ?? "?").slice(0, 2).toUpperCase()}
            </span>
            <span className="truncate flex-1 text-left text-xs">
              {user?.email}
            </span>
            <span
              aria-hidden="true"
              className="flex-shrink-0 text-xs opacity-60"
            >
              ▾
            </span>
          </button>

          {profileOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setProfileOpen(false)}
                aria-hidden="true"
              />
              <div
                className={clsx(
                  "absolute left-0 right-0 bottom-full mb-1 z-50",
                  "bg-[var(--ws-surface)] border border-[var(--ws-border)]",
                  "rounded shadow-lg overflow-hidden",
                )}
              >
                {currentClient?.slug && (
                  <Link
                    to={buildWorkspacePath(currentClient.slug, "settings")}
                    onClick={() => setProfileOpen(false)}
                    className={clsx(
                      "flex items-center gap-2 px-3 py-2 text-sm",
                      "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)]",
                      "hover:bg-[var(--ws-surface-subtle)] transition-colors",
                    )}
                  >
                    <Settings className="h-4 w-4 flex-shrink-0" />
                    <span>Settings</span>
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(false);
                    void logout();
                  }}
                  className={clsx(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm",
                    "text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text)]",
                    "hover:bg-[var(--ws-surface-subtle)] transition-colors",
                  )}
                >
                  <LogOut className="h-4 w-4 flex-shrink-0" />
                  <span>Sign out</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* HelpDrawer — rendered outside the sidebar flow, fixed overlay */}
      <HelpDrawer isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </aside>
  );
}

export default Sidebar;
